import type { ReactNode } from "react"

export interface TooltipViewProps {
  /**
   * The content to display in the tooltip.
   * Can be a ReactNode for flexible content, or use title/description for structured content.
   */
  children: ReactNode

  /**
   * Horizontal position as a percentage (0-100) of the parent container width.
   * The tooltip will be centered at this position.
   */
  leftPercent: number

  /**
   * Optional className for the tooltip container
   */
  className?: string

  /**
   * Optional z-index override
   */
  zIndex?: number
}

/**
 * Pure View component for displaying tooltips.
 * This is a stateless, reusable tooltip component that can be used throughout the application.
 * The tooltip appears above the target element by default.
 */
export const TooltipView = ({ children, leftPercent, className, zIndex = 50 }: TooltipViewProps) => {
  return (
    <div
      className={`pointer-events-none absolute bottom-full mb-2 block w-max ${className ?? ""}`}
      style={{
        left: `${leftPercent}%`,
        transform: "translateX(-50%)",
        zIndex,
      }}
    >
      <div className="relative flex flex-col items-center rounded-lg border border-p400-light bg-p400 px-3 py-2 text-xs text-t100 shadow-xl">
        {children}
        {/* Tooltip arrow pointing down */}
        <div className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 border-b border-r border-p400-light bg-p400"></div>
      </div>
    </div>
  )
}

